import React, { useCallback, useEffect, useState } from "react";
import { useResizableColumns, ResizableTh, ResetCaption } from "@/components/ui/resizable-table";
import api, { API_URL, toErrMsg, UPLOAD_TIMEOUT_MS } from "@/lib/api";
import { useEngagement } from "@/contexts/EngagementContext";
import { fmtNumber } from "@/lib/format";
import { useI18n } from "@/contexts/I18nContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { Upload, Download, Loader2, AlertTriangle, ChevronDown } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import SourceBadge from "@/components/SourceBadge";
import { jobErrorText } from "@/lib/jobErrorText";   // Step 11d · A2: a failed job's error in the screen's language
import { useSearchParams } from "react-router-dom";

/** Step 11d-3 · Mars find 3: the tab a link asks for — `?upload=ap` (the AP aging worksheet's "Upload" link) or `?kind=ap` opens
 *  the AP tab; anything else the AR tab, as before. */
export function agingKindFromSearch(search) {
  const q = new URLSearchParams(search || "");
  const k = String(q.get("upload") || q.get("kind") || "").toLowerCase();
  return k === "ap" ? "ap" : "ar";
}

export default function Aging() {
  const { t, lang } = useI18n();
  const { current } = useEngagement();
  const [reports, setReports] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [searchParams] = useSearchParams();
  const [kind, setKind] = useState(() => agingKindFromSearch(searchParams.toString()));
  const askedKind = agingKindFromSearch(searchParams.toString());
  useEffect(() => { setKind(askedKind); }, [askedKind]);   // a later link to this page with another ?upload= follows it
  // Extracted so the ternary literal isn't flagged as a translatable key-value
  // map by the Slice 5 string denominator (false positive on `"ar":"hsl(...)"`).
  const chartFill = (k) => `hsl(var(${k === "ar" ? "--chart-1" : "--chart-3"}))`;
  const topPartyCols = useResizableColumns({
    storageKey: "aging/top-parties",
    defaults: [200, 110, 110, 100, 100, 100, 110, 110],
  });

  const engId = current?.id;
  const load = useCallback(async () => {
    if (!engId) return;
    const r = await api.get(`/aging/${engId}`);
    setReports(r.data);
  }, [engId]);

  useEffect(() => { load(); }, [load]);

  const onUpload = async (e, k) => {
    const f = e.target.files?.[0];
    if (!f || !current) return;
    const fd = new FormData();
    fd.append("file", f);
    fd.append("kind", k);
    setUploading(true);
    try {
      const r = await api.post(`/aging/${current.id}/upload`, fd,
        { headers: { "Content-Type": "multipart/form-data" },
          timeout: UPLOAD_TIMEOUT_MS });
      // Aging upload runs as a background job — poll /jobs/{id} until done.
      const jobId = r.data?.job_id;
      if (!jobId) {
        // Legacy sync response — refresh immediately.
        toast.success(`${k.toUpperCase()} aging uploaded`);
        await load();
        return;
      }
      const poll = async () => {
        try {
          const jr = await api.get(`/jobs/${jobId}`, { _silent: true });
          if (jr.data.status === "done") {
            toast.success(`${k.toUpperCase()} aging uploaded`);
            setUploading(false);
            await load();
            return;
          }
          if (jr.data.status === "failed") {
            toast.error(jobErrorText(jr.data, lang, t, "Aging upload failed"));
            setUploading(false);
            return;
          }
          setTimeout(poll, 1200);
        } catch { setUploading(false); }
      };
      setTimeout(poll, 800);
      // Bail out of the outer finally block early — poller owns setUploading now.
      e.target.value = "";
      return;
    } catch (err) {
      toast.error(toErrMsg(err, "Upload failed"));
    } finally {
      // Only reset here if we didn't hand off to the poller above.
      setUploading((prev) => prev);
      e.target.value = "";
    }
  };

  const downloadTemplate = async () => {
    const token = localStorage.getItem("af_token");
    const resp = await fetch(`${API_URL}/aging/template`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await resp.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = "AzexAudit_Aging_Template.xlsx"; a.click();
    URL.revokeObjectURL(a.href);
  };

  const exportAnalysis = async (k, fmt) => {
    if (!current) return;
    const token = localStorage.getItem("af_token");
    try {
      const resp = await fetch(
        `${API_URL}/aging/${current.id}/export?kind=${k}&fmt=${fmt}`,
        { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) {
        let msg = t("agingActions.exportFailed") + ` (${resp.status})`;
        try {
          const j = await resp.json();
          if (j?.detail) msg = j.detail;
        } catch { /* non-JSON error body */ }
        toast.error(msg);
        return;
      }
      const blob = await resp.blob();
      const cd = resp.headers.get("Content-Disposition") || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const fname = (m && m[1]) || `aging_${k}.${fmt}`;
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = fname;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success(t("agingActions.exported").replace("{kind}", k.toUpperCase()).replace("{fmt}", fmt.toUpperCase()));
    } catch (err) {
      toast.error(toErrMsg(err, t("agingActions.exportFailed")));
    }
  };

  if (!current) return <div className="text-sm text-muted-foreground">{t("common.noEng")}</div>;

  const arReport = reports.find((r) => r.kind === "ar");
  const apReport = reports.find((r) => r.kind === "ap");

  const render = (report, k) => (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">
            {k === "ar" ? t("aging.uploadAr") : t("aging.uploadAp")}
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid={`aging-template-${k}`}>
              <Download className="me-1 h-3 w-3" /> {t("aging.downloadTemplate")}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!report}
                  data-testid={`aging-export-${k}`}
                >
                  <Download className="me-1 h-3 w-3" />
                  {t("agingExtras.downloadAs")}
                  <ChevronDown className="ms-1 h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => exportAnalysis(k, "csv")}
                  data-testid={`aging-export-${k}-csv`}
                >
                  {t("agingExtras.downloadCsv")}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => exportAnalysis(k, "xls")}
                  data-testid={`aging-export-${k}-xls`}
                >
                  {t("agingExtras.downloadXls")}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => exportAnalysis(k, "xlsx")}
                  data-testid={`aging-export-${k}-xlsx`}
                >
                  {t("agingExtras.downloadXlsx")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <label className="inline-flex cursor-pointer items-center gap-2 rounded-md bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-800">
              {uploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
              <span>{uploading ? t("common.loading") : t("digitalAudit.upload")}</span>
              <input type="file" accept=".csv,.xlsx,.xls,.xlsm" className="hidden"
                     onChange={(e) => onUpload(e, k)} data-testid={`aging-upload-${k}`}
                     disabled={uploading} />
            </label>
          </div>
        </CardHeader>
        <CardContent>
          <div className="text-xs text-muted-foreground">{t("aging.templateNote")}</div>
        </CardContent>
      </Card>

      {!report && (
        <Card>
          <CardContent className="p-8 text-center text-sm text-muted-foreground">
            {t("aging.noData")}
          </CardContent>
        </Card>
      )}

      {report && (
        <>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <Card><CardContent className="p-4">
              <div className="text-xs uppercase tracking-wider text-muted-foreground">{t("agingExtras.totalOutstanding")}</div>
              <div className="mt-2 text-2xl font-bold num-cell">{fmtNumber(report.total, lang)}</div>
            </CardContent></Card>
            <Card><CardContent className="p-4">
              <div className="text-xs uppercase tracking-wider text-muted-foreground">{t("agingExtras.rowsImported")}</div>
              <div className="mt-2 text-2xl font-bold">{report.rows}</div>
            </CardContent></Card>
            <Card><CardContent className="p-4">
              <div className="text-xs uppercase tracking-wider text-muted-foreground">{t("agingExtras.overdue90")}</div>
              <div className="mt-2 text-2xl font-bold num-cell text-red-600">
                {fmtNumber(report.buckets.find((b) => b.bucket === "90+")?.amount || 0, lang)}
              </div>
            </CardContent></Card>
          </div>

          {report.unknown && report.unknown.count > 0 && (
            <Card
              data-testid={`aging-unknown-${k}`}
              className="border-amber-300 bg-amber-50/70"
            >
              <CardContent className="flex flex-col gap-2 p-4 md:flex-row md:items-center md:justify-between">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" />
                  <div>
                    <div className="text-sm font-semibold text-amber-900">
                      {t("aging.unknownLabel")}
                    </div>
                    <div className="text-xs text-amber-800/90">
                      {t("aging.unknownHint")}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-6 md:justify-end">
                  <div className="text-end">
                    <div className="text-[10px] uppercase tracking-wider text-amber-800/70">
                      {t("agingExtras.invoices")}
                    </div>
                    <div
                      className="text-lg font-semibold text-amber-900"
                      data-testid={`aging-unknown-count-${k}`}
                    >
                      {fmtNumber(report.unknown.count, lang)}
                    </div>
                  </div>
                  <div className="text-end">
                    <div className="text-[10px] uppercase tracking-wider text-amber-800/70">
                      {t("agingExtras.amount")}
                    </div>
                    <div
                      className="num-cell text-lg font-semibold text-amber-900"
                      data-testid={`aging-unknown-amount-${k}`}
                    >
                      {fmtNumber(report.unknown.amount, lang)}
                    </div>
                  </div>
                  <div className="text-end">
                    <div className="text-[10px] uppercase tracking-wider text-amber-800/70">
                      {t("agingExtras.pctOfTotal")}
                    </div>
                    <div className="text-lg font-semibold text-amber-900">
                      {report.unknown.share_pct}%
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader><CardTitle className="text-base">{t("agingExtras.bucketBreakdown")}</CardTitle></CardHeader>
            <CardContent className="h-64 min-h-[256px]">
              <ResponsiveContainer width="100%" height="100%" minHeight={220}>
                <BarChart data={report.buckets}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="bucket" fontSize={11} />
                  <YAxis fontSize={11} />
                  <Tooltip />
                  <Bar dataKey="amount" fill={chartFill(k)} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">{t("agingExtras.topParties")}</CardTitle></CardHeader>
            <CardContent className="p-0 max-h-[420px] overflow-y-auto sticky-header">
              <div className="overflow-x-auto min-w-0">
              <table className="text-sm"
                     style={{ tableLayout: "fixed",
                              width: topPartyCols.colWidths.reduce((a,b)=>a+b,0) }}>
                <ResetCaption cols={topPartyCols} />
                <colgroup>
                  {topPartyCols.colWidths.map((w, i) => (
                    <col key={i} style={{ width: w }} />
                  ))}
                </colgroup>
                <thead className="border-b bg-slate-50 text-xs uppercase text-muted-foreground">
                  <tr>
                    <ResizableTh index={0} onResizeStart={topPartyCols.onResizeStart}
                                  className="px-3 py-2 text-start">{t("aging.party")}</ResizableTh>
                    <ResizableTh index={1} onResizeStart={topPartyCols.onResizeStart}
                                  className="px-3 py-2 text-end">{t("agingExtras.total")}</ResizableTh>
                    <ResizableTh index={2} onResizeStart={topPartyCols.onResizeStart}
                                  className="px-3 py-2 text-end">{t("agingExtras.current")}</ResizableTh>
                    <ResizableTh index={3} onResizeStart={topPartyCols.onResizeStart}
                                  className="px-3 py-2 text-end">1-30</ResizableTh>
                    <ResizableTh index={4} onResizeStart={topPartyCols.onResizeStart}
                                  className="px-3 py-2 text-end">31-60</ResizableTh>
                    <ResizableTh index={5} onResizeStart={topPartyCols.onResizeStart}
                                  className="px-3 py-2 text-end">61-90</ResizableTh>
                    <ResizableTh index={6} onResizeStart={topPartyCols.onResizeStart}
                                  className="px-3 py-2 text-end">90+</ResizableTh>
                    <ResizableTh index={7} onResizeStart={topPartyCols.onResizeStart}
                                  className="px-3 py-2 text-end text-amber-700">{t("aging.unknownCol")}</ResizableTh>
                  </tr>
                </thead>
                <tbody>
                  {report.by_party.map((p, i) => (
                    <tr key={i} className="border-b">
                      <td className="px-3 py-1.5 font-medium">{p.party}</td>
                      <td className="px-3 py-1.5 num-cell">{fmtNumber(p.total, lang)}</td>
                      <td className="px-3 py-1.5 num-cell">{fmtNumber(p.Current, lang)}</td>
                      <td className="px-3 py-1.5 num-cell">{fmtNumber(p["1-30"], lang)}</td>
                      <td className="px-3 py-1.5 num-cell">{fmtNumber(p["31-60"], lang)}</td>
                      <td className="px-3 py-1.5 num-cell">{fmtNumber(p["61-90"], lang)}</td>
                      <td className={`px-3 py-1.5 num-cell ${p["90+"] > 0 ? "text-red-600 font-medium" : ""}`}>{fmtNumber(p["90+"], lang)}</td>
                      <td className={`px-3 py-1.5 num-cell ${(p.Unknown || 0) > 0 ? "text-amber-700 font-medium" : "text-muted-foreground"}`}>
                        {fmtNumber(p.Unknown || 0, lang)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>);

  return (
    <div className="space-y-6" data-testid="aging-page">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">{t("aging.title")} <SourceBadge source={reports?.[0]?.source} className="ms-2 align-middle" /></h1>
        <p className="text-sm text-muted-foreground">{t("aging.subtitle")}</p>
      </div>

      <Tabs value={kind} onValueChange={setKind}>
        <TabsList>
          <TabsTrigger value="ar" data-testid="tab-ar">{t("aging.arTab")}</TabsTrigger>
          <TabsTrigger value="ap" data-testid="tab-ap">{t("aging.apTab")}</TabsTrigger>
        </TabsList>
        <TabsContent value="ar">{render(arReport, "ar")}</TabsContent>
        <TabsContent value="ap">{render(apReport, "ap")}</TabsContent>
      </Tabs>
    </div>
  );
}
